import * as React from "react"
import { styled, useTheme } from "@mui/material/styles"
import MuiAppBar from "@mui/material/AppBar"
import MenuIcon from "@mui/icons-material/Menu"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import LogoutIcon from "@mui/icons-material/Logout"
import AccountCircleIcon from "@mui/icons-material/AccountCircle"
import {
  Box,
  Drawer,
  Toolbar,
  CssBaseline,
  Typography,
  List,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material"
import { useAuth0 } from "@auth0/auth0-react"
import { pagesDashboards } from "constants/constants"
import { Link, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useStateContext } from "contexts/userContext"
import axios from "axios"
import { baseURL } from "constants/api"

const drawerWidth = 240
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: drawerWidth,
  }),
}))

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-start",
}))

const MenuBarStyle = {
  display: "flex",
}

const MobileMenuButton = {
  position: "fixed",
  top: 16,
  left: 16,
  zIndex: 1300,
  display: "none",
  "@media(max-width: 1024px)": {
    display: "block",
  },
}

export function MenuBar() {
  const hebrewDate = new Intl.DateTimeFormat("en-u-ca-hebrew", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).format(new Date())

  const gregorianDate = new Date()
  const gregorianDateStr = gregorianDate.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })

  const theme = useTheme()
  const [open, setOpen] = React.useState(false)
  const [isMobile, setIsMobile] = React.useState(false)
  const [restaurantsWithSettings, setRestaurantsWithSettings] = React.useState([])
  const { t } = useTranslation()
  const { settings, restaurants, restaurant_id, setRestaurant_id, isAdmin } = useStateContext()
  const location = useLocation()

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024)
    }

    checkMobile()
    window.addEventListener("resize", checkMobile)

    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  // Load settings for all restaurants
  React.useEffect(() => {
    const fetchRestaurantsSettings = async () => {
      if (!restaurants || restaurants.length === 0) return

      try {
        const restaurantsWithSettingsPromises = restaurants.map(async (restaurant) => {
          try {
            const response = await axios.get(`${baseURL}/settings/${restaurant.id}`)
            return {
              ...restaurant,
              businessName: response.data?.businessName
                ? `${response.data.businessName} #${restaurant.id}`
                : restaurant.name
                  ? `${restaurant.name} #${restaurant.id}`
                  : `Restaurant #${restaurant.id}`,
            }
          } catch (error) {
            console.error(`Failed to fetch settings for restaurant ${restaurant.id}:`, error)
            return {
              ...restaurant,
              businessName: restaurant.name || `Restaurant #${restaurant.id}`,
            }
          }
        })

        const results = await Promise.all(restaurantsWithSettingsPromises)
        setRestaurantsWithSettings(results)
      } catch (error) {
        console.error("Error fetching restaurants settings:", error)
        setRestaurantsWithSettings(restaurants)
      }
    }

    fetchRestaurantsSettings()
  }, [restaurants])

  const handleDrawerToggle = () => {
    setOpen(!open)
  }

  const handleDrawerClose = () => {
    setOpen(false)
  }

  const handleMenuItemClick = () => {
    if (isMobile) {
      handleDrawerClose()
    }
  }

  const { isAuthenticated, logout } = useAuth0()
  const { setIsLoggingOut } = useStateContext()

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true)
      localStorage.removeItem("restaurant_id")
      localStorage.removeItem("isAdmin")
      setRestaurant_id(null)
      await logout({ returnTo: window.location.origin })
    } catch (error) {
      console.error("Logout failed:", error)
    }
  }

  const handleRestaurantChange = (event) => {
    const selectedRestaurantId = event.target.value
    setRestaurant_id(selectedRestaurantId)
  }

  return (
    <>
      {isAuthenticated && (
        <>
          <IconButton sx={MobileMenuButton} onClick={handleDrawerToggle} color="primary">
            <MenuIcon />
          </IconButton>

          <Box sx={MenuBarStyle}>
            <CssBaseline />
            <Drawer
              sx={{
                width: drawerWidth,
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                  width: drawerWidth,
                  boxSizing: "border-box",
                },
                "@media(max-width: 1024px)": {
                  display: open ? "block" : "none",
                },
              }}
              variant={isMobile ? "temporary" : "permanent"}
              anchor="left"
              open={isMobile ? open : true}
              onClose={handleDrawerClose}
              ModalProps={{
                keepMounted: true,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: theme.spacing(3, 2),
                  gap: 1,
                }}
              >
                {settings?.logoImage ? (
                  <Box
                    component="img"
                    src={settings.logoImage}
                    alt="Logo"
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: "50%",
                      backgroundColor: "#FF6B35",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "24px",
                      fontWeight: "bold",
                      color: "white",
                    }}
                  >
                    🍔
                  </Box>
                )}
                <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333" }}>
                  {settings?.businessName || "Burger Bot"}
                </Typography>
              </Box>
              <Divider />

              {/* Account Selector for Admin */}
              {isAdmin && restaurantsWithSettings && restaurantsWithSettings.length > 0 && (
                <Box sx={{ padding: theme.spacing(2) }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="account-select-label">{t("Select Account")}</InputLabel>
                    <Select
                      labelId="account-select-label"
                      id="account-select"
                      value={restaurant_id || ""}
                      label={t("Select Account")}
                      onChange={handleRestaurantChange}
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#FF6B35",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#FF6B35",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#FF6B35",
                        },
                      }}
                    >
                      {restaurantsWithSettings.map((restaurant) => (
                        <MenuItem key={restaurant.id} value={restaurant.id}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <AccountCircleIcon sx={{ fontSize: 18, color: "#FF6B35" }} />
                            <Typography variant="body2">{restaurant.businessName}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              <Divider />
              <List>
                {/* list buttons============== */}
                {pagesDashboards.map(
                  (pagesDashboard) =>
                    pagesDashboard.pageType === "up" && (
                      <Link
                        key={pagesDashboard.name}
                        onClick={handleDrawerClose}
                        to={pagesDashboard.href}
                        style={{ textDecoration: "none" }}
                      >
                        <ListItem disablePadding>
                          <ListItemButton
                            onClick={() => handleMenuItemClick()}
                            sx={{
                              backgroundColor:
                                location.pathname === pagesDashboard.href ? "rgba(255, 107, 53, 0.08)" : "transparent",
                              borderLeft:
                                location.pathname === pagesDashboard.href
                                  ? "3px solid #FF6B35"
                                  : "3px solid transparent",
                              "&:hover": {
                                backgroundColor: "rgba(255, 107, 53, 0.12)",
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: location.pathname === pagesDashboard.href ? "#FF6B35" : "inherit",
                              }}
                            >
                              {pagesDashboard.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={t(pagesDashboard.name)}
                              sx={{
                                "& .MuiTypography-root": {
                                  fontWeight: location.pathname === pagesDashboard.href ? 600 : 400,
                                  color: location.pathname === pagesDashboard.href ? "#FF6B35" : "inherit",
                                },
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      </Link>
                    )
                )}
              </List>
              <Divider />
              <List>
                {pagesDashboards.map(
                  (pagesDashboard) =>
                    pagesDashboard.pageType === "down" && (
                      <Link
                        key={pagesDashboard.name}
                        onClick={handleDrawerClose}
                        to={pagesDashboard.href}
                        style={{ textDecoration: "none" }}
                      >
                        <ListItem disablePadding>
                          <ListItemButton
                            onClick={() => handleMenuItemClick()}
                            sx={{
                              backgroundColor:
                                location.pathname === pagesDashboard.href ? "rgba(255, 107, 53, 0.08)" : "transparent",
                              borderLeft:
                                location.pathname === pagesDashboard.href
                                  ? "3px solid #FF6B35"
                                  : "3px solid transparent",
                              "&:hover": {
                                backgroundColor: "rgba(255, 107, 53, 0.12)",
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: location.pathname === pagesDashboard.href ? "#FF6B35" : "inherit",
                              }}
                            >
                              {pagesDashboard.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={t(pagesDashboard.name)}
                              sx={{
                                "& .MuiTypography-root": {
                                  fontWeight: location.pathname === pagesDashboard.href ? 600 : 400,
                                  color: location.pathname === pagesDashboard.href ? "#FF6B35" : "inherit",
                                },
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      </Link>
                    )
                )}
              </List>
              <Divider />
              <List>
                <ListItem disablePadding>
                  <ListItemButton onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText primary={t("Logout")} />
                  </ListItemButton>
                </ListItem>
              </List>
            </Drawer>
          </Box>
        </>
      )}
    </>
  )
}
